import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";
import { useNavigate } from "react-router-dom";
import { QUOTES_CONTINUE_PATH } from "@routing/paths";

import { ContinueYourQuoteModal } from "@components/ContinueYourQuoteModal";

interface ContinueQuoteData {
  effectiveDate: string;
}

interface ContinueQuoteState {
  isModalOpen: boolean;
  canContinueQuote: boolean;
  defaultEffectiveDate?: string;
  policyLocator?: string;
}

interface ContinueQuoteActions {
  showContinueQuoteModal(
    policyLocator: string,
    defaultEffectiveDate?: string,
    canContinueQuote?: boolean
  ): void;
  closeContinueQuoteModal(): void;
  submitContinueQuote(data: ContinueQuoteData): void;
}

const initialState: ContinueQuoteState = {
  isModalOpen: false,
  canContinueQuote: true,
  policyLocator: undefined,
  defaultEffectiveDate: undefined,
};

const ContinueQuoteContext = createContext<
  ContinueQuoteState & ContinueQuoteActions
>({
  ...initialState,
  showContinueQuoteModal: () => {},
  closeContinueQuoteModal: () => {},
  submitContinueQuote: () => {},
});

export const useContinueQuote = () => useContext(ContinueQuoteContext);

export function ContinueQuoteProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const [state, setState] = useState<ContinueQuoteState>(initialState);

  const showContinueQuoteModal = (
    policyLocator: string,
    defaultEffectiveDate?: string,
    canContinueQuote = true
  ) => {
    setState({
      isModalOpen: true,
      canContinueQuote: canContinueQuote,
      policyLocator,
      defaultEffectiveDate,
    });
  };

  const closeContinueQuoteModal = () => {
    setState(initialState);
  };

  const submitContinueQuote = (data: ContinueQuoteData) => {
    if (!state.policyLocator) return;

    setState(initialState);
    navigate(`/${QUOTES_CONTINUE_PATH}/${state.policyLocator}`, {
      state: {
        previousPath: location.pathname,
        effectiveDate: data.effectiveDate,
      },
    });
  };

  return (
    <ContinueQuoteContext.Provider
      value={{
        ...state,
        showContinueQuoteModal,
        closeContinueQuoteModal,
        submitContinueQuote,
      }}>
      {children}
      <ContinueYourQuoteModal />
    </ContinueQuoteContext.Provider>
  );
}
